import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '../api/client';
import type { IssueSeverity, RuleSeveritySetting } from '@audit/shared-types';

const SEVERITY_OPTIONS: IssueSeverity[] = ['critical', 'serious', 'moderate', 'minor'];

const SEVERITY_STYLES: Record<IssueSeverity, string> = {
  critical: 'bg-red-50 text-red-700 border-red-100',
  serious: 'bg-orange-50 text-orange-700 border-orange-100',
  moderate: 'bg-yellow-50 text-yellow-700 border-yellow-100',
  minor: 'bg-gray-50 text-gray-700 border-gray-200',
};

export default function RuleSeverityPage() {
  const queryClient = useQueryClient();

  const { data = [], isLoading, error } = useQuery({
    queryKey: ['rule-severities'],
    queryFn: () => apiClient.get<RuleSeveritySetting[]>('/rule-severities').then((response) => response.data),
  });

  const updateMutation = useMutation({
    mutationFn: async ({ ruleId, severity }: { ruleId: string; severity: IssueSeverity | null }) => {
      return apiClient.put(`/rule-severities/${encodeURIComponent(ruleId)}`, { severity });
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['rule-severities'] });
    },
  });

  if (isLoading) {
    return <p className="text-sm text-gray-500">Loading rule severity settings…</p>;
  }

  if (error) {
    return <p className="text-sm text-red-600">Failed to load rule severity settings.</p>;
  }

  return (
    <div className="space-y-6">
      <div>
        <p className="text-xs font-semibold uppercase tracking-wide text-brand-700">Classification</p>
        <h2 className="text-3xl font-bold text-gray-900 mt-1">Rule Severity</h2>
        <p className="text-sm text-gray-500 mt-2 max-w-3xl">
          Override the default urgency for individual accessibility rules. New audits will use the severity you set here, which makes it easier to categorise failures consistently.
        </p>
      </div>

      {updateMutation.isError && (
        <p className="text-sm text-red-600">Failed to save the rule severity change.</p>
      )}

      <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
          <div>
            <h3 className="text-sm font-semibold text-gray-700">Failure Rules</h3>
            <p className="text-xs text-gray-500 mt-1">Each row controls how that rule should be prioritised in future audits.</p>
          </div>
          <p className="text-xs text-gray-500">{data.length} rules</p>
        </div>

        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-xs text-gray-500 uppercase bg-gray-50">
              <th className="px-6 py-3">Rule</th>
              <th className="px-6 py-3">Severity</th>
              <th className="px-6 py-3">Default</th>
              <th className="px-6 py-3">Seen</th>
              <th className="px-6 py-3">Source</th>
              <th className="px-6 py-3">Action</th>
            </tr>
          </thead>
          <tbody>
            {data.map((rule) => (
              <tr key={rule.ruleId} className="border-t border-gray-100 hover:bg-gray-50 align-top">
                <td className="px-6 py-4">
                  <p className="font-medium text-gray-900">{rule.ruleId}</p>
                  {rule.help && <p className="text-xs text-gray-500 mt-1">{rule.help}</p>}
                </td>
                <td className="px-6 py-4">
                  <span className={`inline-flex items-center rounded-full border px-2.5 py-1 text-xs font-semibold capitalize ${SEVERITY_STYLES[rule.severity]}`}>
                    {rule.severity}
                  </span>
                </td>
                <td className="px-6 py-4 text-gray-600 capitalize">{rule.defaultSeverity}</td>
                <td className="px-6 py-4 text-gray-600">{rule.issueCount}</td>
                <td className="px-6 py-4 text-gray-600">{rule.isCustom ? 'Custom' : 'Default'}</td>
                <td className="px-6 py-4">
                  <div className="flex items-center gap-2">
                    <select
                      value={rule.severity}
                      disabled={updateMutation.isPending}
                      onChange={(event) => {
                        const severity = event.target.value as IssueSeverity;
                        void updateMutation.mutateAsync({ ruleId: rule.ruleId, severity });
                      }}
                      className="rounded-lg border border-gray-300 px-3 py-2 text-sm text-gray-700 bg-white focus:border-brand-500 focus:outline-none"
                    >
                      {SEVERITY_OPTIONS.map((option) => (
                        <option key={option} value={option}>
                          {option}
                        </option>
                      ))}
                    </select>

                    <button
                      type="button"
                      disabled={!rule.isCustom || updateMutation.isPending}
                      onClick={() => void updateMutation.mutateAsync({ ruleId: rule.ruleId, severity: null })}
                      className="text-xs text-gray-600 hover:text-gray-900 disabled:opacity-40"
                    >
                      Reset
                    </button>
                  </div>
                </td>
              </tr>
            ))}
            {data.length === 0 && (
              <tr>
                <td colSpan={6} className="px-6 py-8 text-center text-sm text-gray-500">
                  No audit rules found yet. Run an audit first, then classify rules here.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}